import type { JSONContent } from "@tiptap/core";
import { CARRY_NODE_TYPES, carryOf, withCarry } from "./extensions/blockCarry";
import type { Block } from "@/lib/api";
import { generateUUID } from "@/utils/uuid";
import { referenceForm } from "./extensions/reference";
import { readBorders } from "@/lib/tables/borders";

/**
 * Parse inline content text into TipTap-compatible nodes.
 * Handles: $math$, **bold**, *italic*, __underline__, ~~strike~~, `code`,
 * @cite{key}, @ref{label}, <br>, HTML tags.
 */
function parseInlineContent(text: string): JSONContent[] {
  const nodes: JSONContent[] = [];

  const patterns = [
    { regex: /\$([^$]+)\$/, type: "math" },
    { regex: /<strong>([^<]*)<\/strong>/, type: "bold" },
    { regex: /<b>([^<]*)<\/b>/, type: "bold" },
    { regex: /<em>([^<]*)<\/em>/, type: "italic" },
    { regex: /<i>([^<]*)<\/i>/, type: "italic" },
    { regex: /<u>([^<]*)<\/u>/, type: "underline" },
    { regex: /<s>([^<]*)<\/s>/, type: "strike" },
    { regex: /<code>([^<]*)<\/code>/, type: "code" },
    { regex: /<br\s*\/?>/, type: "hardBreak" },
    // LaTeX-style markers. The natbib citation family, each carrying its mode
    // onto the node and back out (as the per-block content converter does).
    // Flow read only \cite: a \citet showed as raw text, and a citation
    // inserted in a mode was written back as a bare \cite.
    { regex: /\\citep\{([^}]+)\}/, type: "citation", mode: "parenthetical" },
    { regex: /\\citet\{([^}]+)\}/, type: "citation", mode: "textual" },
    { regex: /\\citeauthor\{([^}]+)\}/, type: "citation", mode: "author" },
    { regex: /\\citeyear\{([^}]+)\}/, type: "citation", mode: "year" },
    { regex: /\\cite\{([^}]+)\}/, type: "citation", mode: "bare" },
    // One entry per reference command, each carrying its form back onto the
    // node — the same way the natbib citation modes carry theirs. These used to
    // share one alternation and collapse to \ref on the way out, which turned an
    // author's \pageref (a page number) into a \ref (a table number) silently.
    { regex: /\\eqref\{([^}]+)\}/, type: "reference", form: "eqref" },
    { regex: /\\cref\{([^}]+)\}/, type: "reference", form: "cref" },
    { regex: /\\Cref\{([^}]+)\}/, type: "reference", form: "Cref" },
    { regex: /\\autoref\{([^}]+)\}/, type: "reference", form: "autoref" },
    { regex: /\\pageref\{([^}]+)\}/, type: "reference", form: "pageref" },
    { regex: /\\nameref\{([^}]+)\}/, type: "reference", form: "nameref" },
    { regex: /\\ref\{([^}]+)\}/, type: "reference", form: "ref" },
    { regex: /\\url\{([^}]+)\}/, type: "url" },
    // Body date tokens. `\date{…}` is preamble-only in LaTeX; when AI/user
    // put it mid-paragraph we promote it to an inline date chip (today or
    // fixed) so the canvas shows a real date. Bare `\today` same path.
    { regex: /\\date\{\\today\}/, type: "dateToday" },
    { regex: /\\date\{([^}]+)\}/, type: "dateFixed" },
    { regex: /\\today\b/, type: "dateToday" },
    // Single-arg \href{url} and two-arg \href{url}{text}. Treat single-arg
    // as a bare URL link (matches the LaTeX/Typst export behavior). The
    // single-group capture form sits in `content`; the parser unifies it.
    { regex: /\\href\{([^}]+)\}\{([^}]+)\}/, type: "href2" },
    { regex: /\\href\{([^}]+)\}/, type: "url" },
    { regex: /\\footnote\{([^}]+)\}/, type: "footnote" },
    // Common LaTeX text commands from LML import / paste.
    { regex: /\\emph\{([^}]+)\}/, type: "italic" },
    { regex: /\\textit\{([^}]+)\}/, type: "italic" },
    { regex: /\\textbf\{([^}]+)\}/, type: "bold" },
    { regex: /\\text\{([^}]+)\}/, type: "plainText" },
    // FormatRail-serialised commands the user may also type directly. On
    // load these become TipTap marks (textColor / fontSize / highlight)
    // so the editor canvas renders coloured/sized text instead of raw
    // backslash text. Round-trip stays the same since the marks
    // serialise back to the same LaTeX in nodeContentToText.
    { regex: /\\textcolor\{([^{}]+)\}\{([^{}]+)\}/, type: "textColor" },
    { regex: /\\hl\[([^\]]+)\]\{([^{}]+)\}/, type: "hlColored" },
    { regex: /\\hl\{([^{}]+)\}/, type: "hlYellow" },
    { regex: /\{\\(tiny|scriptsize|footnotesize|small|normalsize|large|Large|LARGE|huge|Huge)\s+([^{}]+)\}/, type: "fontSize" },
    // Inline spacing — emit as discrete atom nodes so the editor canvas
    // shows a visible badge instead of literal `\hspace{1em}` text. The
    // round-trip serialiser in nodeContentToText writes them back.
    { regex: /\\hspace\*?\{([^}]+)\}/, type: "spaceHorizontal" },
    { regex: /\\vspace\*?\{([^}]+)\}/, type: "spaceVertical" },
    // Consume the optional `{}` we emit after the command name so it
    // doesn't bleed into the next text node.
    { regex: /\\hfill(?:\{\})?/, type: "hfill" },
    { regex: /\\vfill(?:\{\})?/, type: "vfill" },
    { regex: /\\smallskip(?:\{\})?/, type: "smallskip" },
    { regex: /\\medskip(?:\{\})?/, type: "medskip" },
    { regex: /\\bigskip(?:\{\})?/, type: "bigskip" },
    // Markdown syntax
    { regex: /\*\*([^*]+)\*\*/, type: "bold" },
    { regex: /(?<!\*)\*([^*]+)\*(?!\*)/, type: "italic" },
    { regex: /__([^_]+)__/, type: "underline" },
    { regex: /~~([^~]+)~~/, type: "strike" },
    { regex: /`([^`]+)`/, type: "code" },
    // The markers the editor writes for these marks (nodeContentToText), read back as the same
    // patterns the preview (render-markers) and the LaTeX export use. Without them a superscript,
    // subscript or highlight came back after a reload as literal ^x^, %%x%%, ==x== (16 Oct).
    { regex: /\^\^([^^]+)\^\^/, type: "smallcaps" },
    { regex: /(?<!\^)\^([^^\s][^^]*?)\^(?!\^)/, type: "superscript" },
    { regex: /%%(.+?)%%/, type: "subscript" },
    { regex: /==([^=]+)==/, type: "hlYellow" },
    // Comment marker — `[%…%]`, must run after the other paired markers
    // so it doesn't accidentally match inside their content. Lazy
    // capture so adjacent comments don't merge.
    { regex: /\[%(.+?)%\]/, type: "comment" },
    // Lilia-style markers
    { regex: /@cite\{([^}]+)\}/, type: "citation" },
    { regex: /@ref\{([^}]+)\}/, type: "reference", form: "ref" },
  ];

  let remaining = text;

  while (remaining.length > 0) {
    let earliestMatch: {
      index: number;
      length: number;
      content: string;
      group2: string | undefined;
      type: string;
      form?: string;
      mode?: string;
    } | null = null;

    for (const pattern of patterns) {
      const match = remaining.match(pattern.regex);
      if (match && match.index !== undefined) {
        if (earliestMatch === null || match.index < earliestMatch.index) {
          earliestMatch = {
            index: match.index,
            length: match[0].length,
            content: match[1] || "",
            group2: match[2],
            type: pattern.type,
            form: (pattern as { form?: string }).form,
            mode: (pattern as { mode?: string }).mode,
          };
        }
      }
    }

    if (earliestMatch === null) {
      if (remaining) nodes.push({ type: "text", text: remaining });
      break;
    }

    if (earliestMatch.index > 0) {
      nodes.push({ type: "text", text: remaining.slice(0, earliestMatch.index) });
    }

    if (earliestMatch.type === "math") {
      nodes.push({ type: "math", attrs: { latex: earliestMatch.content } });
    } else if (earliestMatch.type === "dateToday") {
      nodes.push({ type: "dateToken", attrs: { mode: "today", value: "" } });
    } else if (earliestMatch.type === "dateFixed") {
      const inner = (earliestMatch.content || "").trim();
      if (!inner || inner === "\\today") {
        nodes.push({ type: "dateToken", attrs: { mode: "today", value: "" } });
      } else {
        nodes.push({ type: "dateToken", attrs: { mode: "fixed", value: inner } });
      }
    } else if (earliestMatch.type === "citation") {
      // @cite{key} has no mode of its own: a plain \cite, as it always was.
      nodes.push({ type: "citation", attrs: { citeKey: earliestMatch.content, mode: earliestMatch.mode ?? "bare" } });
    } else if (earliestMatch.type === "reference") {
      nodes.push({ type: "reference", attrs: { label: earliestMatch.content, form: earliestMatch.form || "ref" } });
    } else if (earliestMatch.type === "url") {
      nodes.push({ type: "inlineUrl", attrs: { href: earliestMatch.content } });
    } else if (earliestMatch.type === "href2") {
      // \href{url}{text} — the visible text as a link to url. An inline link
      // node carrying its text: it used to be a text node with an "inlineUrl"
      // mark, but inlineUrl is a node and there is no such mark — one unknown
      // mark makes the whole document invalid, so the editor opened it empty
      // and the next save wrote the empty document back over it.
      nodes.push({
        type: "inlineUrl",
        attrs: { href: earliestMatch.content, text: earliestMatch.group2 ?? "" },
      });
    } else if (earliestMatch.type === "footnote") {
      nodes.push({ type: "footnote", attrs: { content: earliestMatch.content, number: 0 } });
    } else if (earliestMatch.type === "hardBreak") {
      nodes.push({ type: "hardBreak" });
    } else if (earliestMatch.type === "textColor") {
      // \textcolor{NAME}{TEXT} → text node with textColor mark.
      nodes.push({
        type: "text",
        text: earliestMatch.group2 ?? "",
        marks: [{ type: "textColor", attrs: { color: earliestMatch.content } }],
      });
    } else if (earliestMatch.type === "hlColored") {
      // \hl[COLOR]{TEXT} → highlight mark with color attr.
      nodes.push({
        type: "text",
        text: earliestMatch.group2 ?? "",
        marks: [{ type: "highlight", attrs: { color: earliestMatch.content } }],
      });
    } else if (earliestMatch.type === "hlYellow") {
      // \hl{TEXT} → default-yellow highlight mark.
      nodes.push({
        type: "text",
        text: earliestMatch.content,
        marks: [{ type: "highlight", attrs: { color: "yellow" } }],
      });
    } else if (earliestMatch.type === "spaceHorizontal") {
      nodes.push({ type: "spacingCommand", attrs: { kind: "hspace", arg: earliestMatch.content } });
    } else if (earliestMatch.type === "spaceVertical") {
      nodes.push({ type: "spacingCommand", attrs: { kind: "vspace", arg: earliestMatch.content } });
    } else if (earliestMatch.type === "hfill" || earliestMatch.type === "vfill" ||
               earliestMatch.type === "smallskip" || earliestMatch.type === "medskip" ||
               earliestMatch.type === "bigskip") {
      nodes.push({ type: "spacingCommand", attrs: { kind: earliestMatch.type, arg: "" } });
    } else if (earliestMatch.type === "fontSize") {
      // {\large TEXT} → text node with fontSize mark sized as the
      // captured size keyword (matches the FormatRail picker).
      nodes.push({
        type: "text",
        text: earliestMatch.group2 ?? "",
        marks: [{ type: "fontSize", attrs: { size: earliestMatch.content } }],
      });
    } else if (earliestMatch.type === "plainText") {
      nodes.push({
        type: "text",
        text: earliestMatch.content,
      });
    } else {
      // Formatting marks (bold/italic including \emph → italic)
      nodes.push({
        type: "text",
        text: earliestMatch.content,
        marks: [{ type: earliestMatch.type }],
      });
    }

    remaining = remaining.slice(earliestMatch.index + earliestMatch.length);
  }

  return nodes;
}

/** A citation's mode as its natbib command; "bare" is a plain \cite. */
function citationCommand(mode: string | undefined): string {
  switch (mode || "parenthetical") {
    case "textual": return "citet";
    case "author": return "citeauthor";
    case "year": return "citeyear";
    case "bare": return "cite";
    default: return "citep";
  }
}

/**
 * Convert TipTap node content back to text with formatting markers.
 */
// Whitespace-aware wrapper — keeps leading/trailing whitespace OUTSIDE
// the wrapper. Browser double-click word selection extends past the
// word into a space; without this, the LaTeX source picks up the
// stray space: `\textbf{foo }` instead of `\textbf{foo} `.
function wrapMark(text: string, prefix: string, suffix: string): string {
  const m = text.match(/^(\s*)([\s\S]*?)(\s*)$/);
  if (!m) return prefix + text + suffix;
  const [, leading, core, trailing] = m;
  if (!core) return text;
  return leading + prefix + core + suffix + trailing;
}

export function nodeContentToText(content: JSONContent[]): string {
  let result = "";

  for (const node of content) {
    if (node.type === "text") {
      let text = node.text || "";
      if (node.marks) {
        for (const mark of node.marks) {
          switch (mark.type) {
            case "bold": text = wrapMark(text, "**", "**"); break;
            case "italic": text = wrapMark(text, "*", "*"); break;
            case "underline": text = wrapMark(text, "__", "__"); break;
            case "strike": text = wrapMark(text, "~~", "~~"); break;
            case "code": text = wrapMark(text, "`", "`"); break;
            case "superscript": text = wrapMark(text, "^", "^"); break;
            case "subscript": text = wrapMark(text, "%%", "%%"); break;
            case "smallcaps": text = wrapMark(text, "^^", "^^"); break;
            case "highlight": {
              // Default yellow keeps the compact ==…== markdown for
              // back-compat; non-yellow writes `\hl[color]{…}` LaTeX
              // directly. Round-trips through the C# LmlConversionService
              // unchanged (no regex strips it).
              const color = (mark.attrs?.color as string | undefined) || "yellow";
              text = color === "yellow"
                ? wrapMark(text, "==", "==")
                : wrapMark(text, `\\hl[${color}]{`, "}");
              break;
            }
            case "textColor": {
              // No markdown convention for color — write the LaTeX
              // command directly. xcolor handles the named lookup;
              // render-markers.ts maps it to inline `<span style="color:…">`
              // for preview.
              const color = (mark.attrs?.color as string | undefined) || "red";
              text = wrapMark(text, `\\textcolor{${color}}{`, "}");
              break;
            }
            case "fontSize": {
              // LaTeX size commands are paragraph-level switches; the
              // `{\large …}` group form scopes the change to the
              // wrapped text only. normalsize is the doc default →
              // skip the wrap so untouched text doesn't churn.
              const size = (mark.attrs?.size as string | undefined) || "normalsize";
              if (size && size !== "normalsize") {
                text = wrapMark(text, `{\\${size} `, "}");
              }
              break;
            }
            case "comment":
              // `[%…%]` — paired with the inline-content parser above
              // and the C# `\iffalse … \fi` LaTeX rewrite. Wrap the
              // core only so leading/trailing whitespace stays outside.
              text = wrapMark(text, "[%", "%]");
              break;
          }
        }
      }
      result += text;
    } else if (node.type === "math") {
      result += `$${node.attrs?.latex || ""}$`;
    } else if (node.type === "dateToken") {
      // Body dates never serialise as preamble `\date{…}` — that is a no-op
      // mid-paragraph. Live → `\today`; fixed → plain printable text.
      const mode = (node.attrs?.mode as string) || "today";
      const value = ((node.attrs?.value as string) || "").trim();
      if (mode === "today" || !value || value === "\\today") {
        result += "\\today";
      } else {
        const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
        if (iso) {
          const d = new Date(Date.UTC(+iso[1], +iso[2] - 1, +iso[3]));
          result += d.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
            timeZone: "UTC",
          });
        } else {
          result += value;
        }
      }
    } else if (node.type === "citation") {
      result += `\\${citationCommand(node.attrs?.mode as string | undefined)}{${node.attrs?.citeKey || ""}}`;
    } else if (node.type === "reference") {
      result += `\\${referenceForm(node.attrs?.form)}{${node.attrs?.label || ""}}`;
    } else if (node.type === "inlineUrl") {
      const text = (node.attrs?.text as string | undefined) || "";
      result += text ? `\\href{${node.attrs?.href || ""}}{${text}}` : `\\url{${node.attrs?.href || ""}}`;
    } else if (node.type === "footnote") {
      result += `\\footnote{${node.attrs?.content || ""}}`;
    } else if (node.type === "hardBreak") {
      result += "\n";
    } else if (node.type === "spacingCommand") {
      // Inline spacing atom — serialise back to its LaTeX form. For the
      // no-arg commands (\hfill / \vfill / *skip) we append `{}` rather
      // than a trailing space so LaTeX's command-name lookup terminates
      // cleanly. A bare space gets eaten by trim/normalise passes in
      // the save pipeline (observed 2026-05-14: `\hfill is` → stored
      // as `\hfillis` → undefined-command at compile).
      const kind = (node.attrs?.kind as string | undefined) || "hspace";
      const arg = (node.attrs?.arg as string | undefined) || "";
      if (kind === "hspace" || kind === "vspace") {
        result += `\\${kind}{${arg || "1em"}}`;
      } else {
        result += `\\${kind}{}`;
      }
    }
  }

  return result;
}

/**
 * Convert Block[] from API into a full TipTap document JSON.
 */
/**
 * Alignment (Block menu, Mod-Shift-L/E/R/J): saved as `align` when not left, as the block editor's
 * content converter does. The key is always written, undefined for left, so choosing left again
 * drops an `align` the block carried in from the server.
 */
const ALIGNS = new Set(["center", "right", "justify"]);
function savedAlign(node: JSONContent): string | undefined {
  const a = node.attrs?.textAlign;
  return typeof a === "string" && ALIGNS.has(a) ? a : undefined;
}
function alignAttr(align: unknown): { textAlign?: string } {
  return typeof align === "string" && ALIGNS.has(align) ? { textAlign: align } : {};
}

/**
 * Blocks with a canvas view but no fields of their own in this serializer: the node's attributes
 * are the block's content, under the same names (a verse's `lines` are its `text`, one per line).
 * Without these cases they loaded as paragraphs and saved back as paragraphs: an imported verse,
 * aside or cover lost its type on the document's first save (16 Oct).
 */
const ATTR_BLOCKS: Record<string, { node: string; attrs: Record<string, unknown> }> = {
  verse: { node: "verseBlock", attrs: { lines: [], attribution: "", title: "" } },
  aside: { node: "asideBlock", attrs: { text: "", title: "", asideType: "sidebar" } },
  annotation: { node: "annotationBlock", attrs: { text: "", originalContent: "", annotationType: "comment", status: "pending" } },
  cover: { node: "coverBlock", attrs: { src: "", title: "", subtitle: "", author: "" } },
  chapterBreak: { node: "chapterBreakBlock", attrs: { label: "" } },
  frontMatter: { node: "frontMatterBlock", attrs: { text: "", subType: "titlePage" } },
  backMatter: { node: "backMatterBlock", attrs: { text: "", subType: "glossary", entries: [] } },
};
/** A block type the editor has its own node for (so a paragraph carrying it was converted). */
function modelledType(type: string): boolean {
  return type in ATTR_BLOCKS || Object.values(NODE_TO_BLOCK_TYPE).includes(type);
}
const ATTR_BLOCK_OF_NODE = Object.fromEntries(Object.entries(ATTR_BLOCKS).map(([type, b]) => [b.node, type]));

function attrBlockToNode(type: string, block: Block, c: Record<string, unknown>): JSONContent {
  const spec = ATTR_BLOCKS[type];
  const attrs: Record<string, unknown> = { blockId: block.id };
  for (const key of Object.keys(spec.attrs)) if (c[key] !== undefined) attrs[key] = c[key];
  if (type === "verse" && !Array.isArray(c.lines) && typeof c.text === "string") attrs.lines = c.text.split("\n");
  return { type: spec.node, attrs };
}

/** Only what the block had, or what the editor changed from the default: no fields made up. */
function attrNodeToBlock(type: string, node: JSONContent, blockId: string, sortOrder: number): Block {
  const spec = ATTR_BLOCKS[type];
  const carried = ((node.attrs?.carry as { content?: Record<string, unknown> } | undefined)?.content) ?? {};
  const content: Record<string, unknown> = {};
  for (const [key, dflt] of Object.entries(spec.attrs)) {
    const v = node.attrs?.[key];
    if (v === undefined) continue;
    if (type === "verse" && key === "lines") {
      const text = (v as string[]).join("\n");
      if ("text" in carried || text) content.text = text;
      if ("lines" in carried) content.lines = v;
      continue;
    }
    if (key in carried || JSON.stringify(v) !== JSON.stringify(dflt)) content[key] = v;
  }
  return { id: blockId, type, content, sortOrder } as Block;
}

export function blocksToTiptapDoc(blocks: Block[]): JSONContent {
  const content: JSONContent[] = [];

  for (const block of blocks) {
    const node = blockToTiptapNode(block);
    if (node) content.push(node);
  }

  // Ensure at least one paragraph
  if (content.length === 0) {
    content.push({ type: "paragraph", content: [] });
  }

  return { type: "doc", content };
}

/**
 * Convert a single Block to a TipTap node.
 */
/**
 * A block as an editor node. What the editor does not model rides along in
 * `carry` (extensions/blockCarry) so a save gives it back.
 */
export function blockToTiptapNode(block: Block): JSONContent | null {
  const node = blockToTiptapNodeInner(block);
  if (!node || node.type === "figureBlock" || !CARRY_NODE_TYPES.includes(node.type ?? "")) return node;
  // A type shown as a paragraph carries its type even with nothing else (a divider's {}).
  const carry = carryOf(block.type, block.content)
    ?? (node.type === "paragraph" && block.type !== "paragraph" ? { type: block.type, content: {} } : null);
  return carry ? { ...node, attrs: { ...(node.attrs ?? {}), carry } } : node;
}

function blockToTiptapNodeInner(block: Block): JSONContent | null {
  const c = block.content || {};

  switch (block.type) {
    case "paragraph": {
      const text = (c.text as string) || "";
      const paragraphs = text.split("\n");
      // Multi-line paragraphs use hardBreaks within a single paragraph node
      const inlineContent: JSONContent[] = [];
      for (let i = 0; i < paragraphs.length; i++) {
        if (i > 0) inlineContent.push({ type: "hardBreak" });
        const parsed = parseInlineContent(paragraphs[i]);
        inlineContent.push(...parsed);
      }
      return {
        type: "paragraph",
        attrs: { blockId: block.id, ...alignAttr(c.align) },
        content: inlineContent.length > 0 ? inlineContent : undefined,
      };
    }

    case "heading": {
      const text = (c.text as string) || "";
      const level = (c.level as number) || 1;
      // numbered defaults true; only thread the attr when the doc
      // explicitly set false (matches the API render check on
      // ValueKind == False so JSON nulls keep numbered behavior).
      const numbered = c.numbered === false ? false : true;
      const shortTitle = (c.shortTitle as string) || "";
      const inlineContent = parseInlineContent(text);
      return {
        type: "heading",
        attrs: { level, blockId: block.id, numbered, shortTitle },
        content: inlineContent.length > 0 ? inlineContent : undefined,
      };
    }

    case "equation":
      return {
        type: "equationBlock",
        attrs: {
          blockId: block.id,
          latex: (c.latex as string) || "",
          equationMode: (c.equationMode as string) || "display",
          label: (c.label as string) || "",
          numbered: c.numbered !== false,
        },
      };

    case "figure": {
      const { src, caption, alt, assetId, label, position, placement, width, subfigures, kind, source, plot, span, wide, fullWidth, ...extra } = c;
      return {
        type: "figureBlock",
        attrs: {
          blockId: block.id,
          src: (src as string) || "",
          caption: (caption as string) || "",
          alt: (alt as string) || "",
          assetId: assetId ?? null,
          label: label ?? null,
          position: position ?? null,
          placement: placement ?? null,
          span: fullWidthOf({ span, wide, fullWidth }),
          width: width ?? null,
          subfigures: subfigures ?? null,
          // TikZ figures: absent stays absent (an image figure).
          kind: kind ?? null,
          source: source ?? null,
          // A plot linked to a table (step 4a); absent on every other figure.
          plot: plot ?? null,
          extra: Object.keys(extra).length ? extra : null,
        },
      };
    }

    case "code":
      return {
        type: "codeBlockNode",
        attrs: {
          blockId: block.id,
          code: (c.code as string) || "",
          // As found: "" is how the import marks a verbatim block, which the
          // view shows as plaintext. It was rewritten to "plaintext" on save.
          language: typeof c.language === "string" ? c.language : "plaintext",
        },
      };

    case "theorem":
      return {
        type: "theoremBlock",
        attrs: {
          blockId: block.id,
          theoremType: (c.theoremType as string) || "theorem",
          title: (c.title as string) || "",
          text: (c.text as string) || "",
          label: (c.label as string) || "",
        },
      };

    case "table":
      return {
        type: "tableBlock",
        attrs: {
          blockId: block.id,
          headers: (c.headers as string[]) || ["Column 1", "Column 2", "Column 3"],
          rows: (c.rows as string[][]) || [["", "", ""], ["", "", ""]],
          caption: (c.caption as string) || "",
          label: (c.label as string) || "",
          columnAlign: (c.columnAlign as string[]) || [],
          columnWidth: (c.columnWidth as string[]) || [],
          columnUnit: Array.isArray(c.columnUnit) ? c.columnUnit : null,
          columnBest: Array.isArray(c.columnBest) ? c.columnBest : null,
          borders: readBorders(c.borders),
          hasHeader: typeof c.hasHeader === "boolean" ? c.hasHeader : null,
          headerGroups: Array.isArray(c.headerGroups) && c.headerGroups.length ? c.headerGroups : null,
          rowGroups: Array.isArray(c.rowGroups) && c.rowGroups.length ? c.rowGroups : null,
          shortCaption: (c.shortCaption as string) || "",
          span: fullWidthOf(c),
        },
      };

    case "abstract":
      return {
        type: "abstractBlock",
        attrs: {
          blockId: block.id,
          title: (c.title as string) || "Abstract",
          text: (c.text as string) || "",
        },
      };

    case "title":
      return {
        type: "titleBlock",
        attrs: {
          blockId: block.id,
          title: (c.title as string) || "",
          author: (c.author as string) || "",
          // Absent stays absent: the title view infers the mode from the date
          // (resolveDateMode). Defaulting it to "today" showed an imported
          // \date{2026} as today's date, and saved it that way.
          dateMode: (c.dateMode as string) || "",
          date: (c.date as string) || "\\today",
          // A list, or one string split at commas or semicolons (as the API reads it).
          keywords: Array.isArray(c.keywords)
            ? (c.keywords as unknown[]).filter((k): k is string => typeof k === "string" && k.trim() !== "").map((k) => k.trim())
            : typeof c.keywords === "string"
              ? c.keywords.split(/[,;]/).map((k) => k.trim()).filter(Boolean)
              : [],
          acmCcs: typeof c.acmCcs === "string" ? c.acmCcs : "",
          acmRights: typeof c.acmRights === "string" ? c.acmRights : "",
        },
      };

    case "bibliography":
      return {
        type: "bibliographyBlock",
        attrs: {
          blockId: block.id,
          title: (c.title as string) || "References",
          style: (c.style as string) || "apa",
        },
      };

    case "tableOfContents":
      return {
        type: "tocBlock",
        attrs: {
          blockId: block.id,
          title: (c.title as string) || "Table of Contents",
        },
      };

    case "pageBreak":
      return {
        type: "pageBreakBlock",
        attrs: { blockId: block.id },
      };

    case "columnBreak":
      return {
        type: "columnBreakBlock",
        attrs: { blockId: block.id },
      };

    case "list":
      return {
        type: "listBlock",
        attrs: {
          blockId: block.id,
          items: (c.items as string[]) || [],
          ordered: (c.ordered as boolean) || false,
          labelFormat: (c.labelFormat as string) || "number",
          start: typeof c.start === "number" ? (c.start as number) : 1,
          kind: (c.kind as string) || null,
          spacing: (c.spacing as string) || "default",
        },
      };

    case "blockquote":
      return {
        type: "blockquoteBlock",
        attrs: {
          blockId: block.id,
          text: (c.text as string) || "",
          variant: (c.variant as string) || "simple",
          attribution: (c.attribution as string) || "",
        },
      };

    case "footnote":
      return {
        type: "footnoteBlockNode",
        attrs: {
          blockId: block.id,
          text: (c.text as string) || "",
        },
      };

    case "embed":
      return {
        type: "embedBlock",
        attrs: {
          blockId: block.id,
          engine: (c.engine as string) || "latex",
          code: (c.code as string) || "",
          caption: (c.caption as string) || "",
          label: (c.label as string) || "",
          previewImage: (c.previewImage as string) || "",
          packages: (c.packages as string[]) || [],
        },
      };

    case "algorithm":
      return {
        type: "algorithmBlock",
        attrs: {
          blockId: block.id,
          caption: (c.caption as string) || "",
          label: (c.label as string) || "",
          lines: (c.lines as unknown[]) || [{ indent: 0, keyword: "", text: "", comment: "" }],
        },
      };

    case "callout":
      return {
        type: "calloutBlock",
        attrs: {
          blockId: block.id,
          calloutType: (c.calloutType as string) || "note",
          title: (c.title as string) || "",
          text: (c.text as string) || "",
          collapsible: (c.collapsible as boolean) || false,
          color: (c.color as string) || "",
        },
      };

    case "personalInfo":
      return {
        type: "personalInfoBlock",
        attrs: {
          blockId: block.id,
          name: (c.name as string) || "",
          headline: (c.headline as string) || "",
          email: (c.email as string) || "",
          phones: (c.phones as unknown[]) || [],
          homepage: (c.homepage as string) || "",
          location: (c.location as string) || "",
          socials: (c.socials as unknown[]) || [],
          extra: (c.extra as string) || "",
        },
      };

    case "photo":
      return {
        type: "photoBlock",
        attrs: {
          blockId: block.id,
          src: (c.src as string) || "",
          alt: (c.alt as string) || "",
          shape: (c.shape as string) || "square",
          size: (c.size as number) ?? 64,
          position: (c.position as string) || "right",
          border: (c.border as number) ?? 0,
        },
      };

    case "cvSection":
      return {
        type: "cvSectionBlock",
        attrs: {
          blockId: block.id,
          title: (c.title as string) || "",
        },
      };

    case "cvEntry":
      return {
        type: "cvEntryBlock",
        attrs: {
          blockId: block.id,
          period: (c.period as string) || "",
          role: (c.role as string) || "",
          org: (c.org as string) || "",
          location: (c.location as string) || "",
          highlight: (c.highlight as string) || "",
          description: (c.description as string) || "",
          tech: (c.tech as string[]) || [],
        },
      };

    case "verse":
    case "aside":
    case "annotation":
    case "cover":
    case "chapterBreak":
    case "frontMatter":
    case "backMatter":
      return attrBlockToNode(block.type, block, c);

    default:
      // Unknown block types → paragraph with raw text (its type and content ride in `carry`,
      // and tiptapNodeToBlock gives the block its type back)
      return {
        type: "paragraph",
        attrs: { blockId: block.id },
        content: parseInlineContent((c.text as string) || ""),
      };
  }
}

/**
 * Map TipTap node type names back to Block type names.
 */
export const NODE_TO_BLOCK_TYPE: Record<string, string> = {
  paragraph: "paragraph",
  heading: "heading",
  equationBlock: "equation",
  figureBlock: "figure",
  codeBlockNode: "code",
  theoremBlock: "theorem",
  tableBlock: "table",
  abstractBlock: "abstract",
  titleBlock: "title",
  bibliographyBlock: "bibliography",
  tocBlock: "tableOfContents",
  pageBreakBlock: "pageBreak",
  columnBreakBlock: "columnBreak",
  listBlock: "list",
  blockquoteBlock: "blockquote",
  footnoteBlockNode: "footnote",
  embedBlock: "embed",
  algorithmBlock: "algorithm",
  calloutBlock: "callout",
  personalInfoBlock: "personalInfo",
  photoBlock: "photo",
  cvSectionBlock: "cvSection",
  cvEntryBlock: "cvEntry",
};

/**
 * Serialize TipTap document JSON back to Block[].
 */
/** Keep only keys whose value is set. Null and undefined both mean "not set". */
/** Full width as the API reads it (RenderService.IsFullWidth): span "page", or MCP's wide / fullWidth. */
function fullWidthOf(c: Record<string, unknown>): "page" | null {
  return (typeof c.span === "string" && c.span.toLowerCase() === "page") || c.wide === true || c.fullWidth === true ? "page" : null;
}

function definedOnly(fields: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== null && v !== undefined));
}

export function tiptapDocToBlocks(doc: JSONContent): Block[] {
  const blocks: Block[] = [];
  const seenIds = new Set<string>();

  if (!doc.content) return blocks;

  for (let i = 0; i < doc.content.length; i++) {
    const node = doc.content[i];
    // A split in the middle of a block copies every attribute — blockId and
    // carry included — so the second half arrives as a duplicate. It is a new
    // block: a new id, and none of the original's carried fields (else a
    // heading split in two would give both halves its label).
    const duplicate = typeof node.attrs?.blockId === "string" && seenIds.has(node.attrs.blockId);
    const block = duplicate ? tiptapNodeToBlockInner(node, i) : tiptapNodeToBlock(node, i);
    if (block) {
      // Deduplicate blockIds — TipTap split/join can create nodes with the same blockId
      if (seenIds.has(block.id)) {
        block.id = generateUUID();
      }
      seenIds.add(block.id);
      blocks.push(block);
    }
  }

  return blocks;
}

/**
 * Convert a single TipTap node to a Block.
 */
function tiptapNodeToBlock(node: JSONContent, sortOrder: number): Block | null {
  const attrType = ATTR_BLOCK_OF_NODE[node.type ?? ""];
  const block = attrType
    ? attrNodeToBlock(attrType, node, (node.attrs?.blockId as string) || generateUUID(), sortOrder)
    : tiptapNodeToBlockInner(node, sortOrder);
  if (!block || !node.attrs?.carry) return block;
  const carry = node.attrs.carry as { type?: string; content?: Record<string, unknown> };
  // A block the canvas shows as a paragraph (a divider, a quote, a slide …) keeps its type: its
  // content as loaded, with the text the author typed if it had text or now has some.
  // Only types the editor has no node for: a heading the author made a paragraph stays one.
  if (block.type === "paragraph" && carry.type && carry.content && !modelledType(carry.type)) {
    const text = (block.content as { text?: string }).text ?? "";
    const content = "text" in carry.content || text ? { ...carry.content, text } : { ...carry.content };
    return { ...block, type: carry.type, content } as Block;
  }
  return { ...block, content: withCarry(block.type, (block.content ?? {}) as Record<string, unknown>, node.attrs.carry) };
}

function tiptapNodeToBlockInner(node: JSONContent, sortOrder: number): Block | null {
  const blockType = NODE_TO_BLOCK_TYPE[node.type || ""] || "paragraph";
  const blockId = (node.attrs?.blockId as string) || generateUUID();

  switch (node.type) {
    case "paragraph": {
      const text = node.content ? nodeContentToText(node.content) : "";
      return {
        id: blockId,
        type: "paragraph",
        content: { text, align: savedAlign(node) },
        sortOrder,
      };
    }

    case "heading": {
      const text = node.content ? nodeContentToText(node.content) : "";
      const level = (node.attrs?.level as number) || 1;
      const numbered = node.attrs?.numbered === false ? false : true;
      const shortTitle = (node.attrs?.shortTitle as string) || "";
      return {
        id: blockId,
        type: "heading",
        content: { text, level, numbered, shortTitle },
        sortOrder,
      };
    }

    case "equationBlock":
      return {
        id: blockId,
        type: "equation",
        content: {
          latex: node.attrs?.latex || "",
          equationMode: node.attrs?.equationMode || "display",
          label: node.attrs?.label || "",
          // Modelled, so the edit modal's Numbered box reaches the server.
          // It was not a node attribute: TipTap dropped the update and the
          // checkbox did nothing.
          numbered: node.attrs?.numbered !== false,
        },
        sortOrder,
      };

    case "figureBlock": {
      const a = node.attrs ?? {};
      return {
        id: blockId,
        type: "figure",
        content: {
          // Unknown fields first, so a named field always wins over a stale
          // copy of itself.
          ...((a.extra as Record<string, unknown> | null) ?? {}),
          src: a.src || "",
          caption: a.caption || "",
          alt: a.alt || "",
          assetId: a.assetId ?? null,
          // Written only when set: absent means "the exporter's default".
          ...definedOnly({
            label: a.label,
            position: a.position,
            placement: a.placement,
            span: a.span === "page" ? "page" : null,
            width: a.width,
            subfigures: a.subfigures,
            kind: a.kind,
            source: a.source,
            plot: a.plot,
          }),
        },
        sortOrder,
      };
    }

    case "codeBlockNode":
      return {
        id: blockId,
        type: "code",
        content: {
          code: node.attrs?.code || "",
          language: node.attrs?.language ?? "plaintext",
        },
        sortOrder,
      };

    case "theoremBlock":
      return {
        id: blockId,
        type: "theorem",
        content: {
          theoremType: node.attrs?.theoremType || "theorem",
          title: node.attrs?.title || "",
          text: node.attrs?.text || "",
          label: node.attrs?.label || "",
        },
        sortOrder,
      };

    case "tableBlock":
      return {
        id: blockId,
        type: "table",
        content: {
          headers: node.attrs?.headers || [],
          rows: node.attrs?.rows || [],
          caption: node.attrs?.caption || "",
          label: node.attrs?.label || "",
          columnAlign: node.attrs?.columnAlign || [],
          columnWidth: node.attrs?.columnWidth || [],
          // Written only when the table has them, so a table without saves as before.
          ...(Array.isArray(node.attrs?.columnUnit) ? { columnUnit: node.attrs.columnUnit } : {}),
          ...(Array.isArray(node.attrs?.columnBest) ? { columnBest: node.attrs.columnBest } : {}),
          // The same for the rules preset and the header flag: written when set.
          ...(readBorders(node.attrs?.borders) ? { borders: node.attrs?.borders } : {}),
          ...(typeof node.attrs?.hasHeader === "boolean" ? { hasHeader: node.attrs.hasHeader } : {}),
          // The 2g layout (group headers, row groups): written when the table has
          // it. Undefined, not absent, otherwise: the block's carried content is
          // laid under these fields, and a removed group must not come back from it
          // (undefined is dropped when the block is sent, as absent always was).
          headerGroups: Array.isArray(node.attrs?.headerGroups) && node.attrs.headerGroups.length ? node.attrs.headerGroups : undefined,
          rowGroups: Array.isArray(node.attrs?.rowGroups) && node.attrs.rowGroups.length ? node.attrs.rowGroups : undefined,
          shortCaption: node.attrs?.shortCaption || "",
          // Written when on. Undefined otherwise, with the older spellings, so neither the
          // carried content nor an MCP "wide: true" brings it back once turned off.
          span: node.attrs?.span === "page" ? "page" : undefined,
          wide: undefined,
          fullWidth: undefined,
        },
        sortOrder,
      };

    case "abstractBlock":
      return {
        id: blockId,
        type: "abstract",
        content: {
          title: node.attrs?.title || "Abstract",
          text: node.attrs?.text || "",
        },
        sortOrder,
      };

    case "titleBlock":
      return {
        id: blockId,
        type: "title",
        content: {
          title: node.attrs?.title || "",
          author: node.attrs?.author || "",
          date: node.attrs?.date || "\\today",
          ...definedOnly({ dateMode: node.attrs?.dateMode || null }),
          // Written only when there are keywords, so a title block without them saves as before.
          ...definedOnly({ keywords: (node.attrs?.keywords as string[] | undefined)?.length ? node.attrs?.keywords : null }),
          // ACM's pasted blocks, likewise only when filled.
          ...definedOnly({ acmCcs: (node.attrs?.acmCcs as string | undefined)?.trim() ? node.attrs?.acmCcs : null }),
          ...definedOnly({ acmRights: (node.attrs?.acmRights as string | undefined)?.trim() ? node.attrs?.acmRights : null }),
        },
        sortOrder,
      };

    case "bibliographyBlock":
      return {
        id: blockId,
        type: "bibliography",
        content: {
          title: node.attrs?.title || "References",
          style: node.attrs?.style || "apa",
        },
        sortOrder,
      };

    case "tocBlock":
      return {
        id: blockId,
        type: "tableOfContents",
        content: {
          title: node.attrs?.title || "Table of Contents",
        },
        sortOrder,
      };

    case "pageBreakBlock":
      return {
        id: blockId,
        type: "pageBreak",
        content: {},
        sortOrder,
      };

    case "columnBreakBlock":
      return {
        id: blockId,
        type: "columnBreak",
        content: {},
        sortOrder,
      };

    case "listBlock":
      return {
        id: blockId,
        type: "list",
        content: {
          items: node.attrs?.items || [],
          ordered: node.attrs?.ordered || false,
          labelFormat: node.attrs?.labelFormat || "number",
          start: typeof node.attrs?.start === "number" ? node.attrs.start : 1,
          kind: node.attrs?.kind || null,
          spacing: node.attrs?.spacing || "default",
        },
        sortOrder,
      };

    case "blockquoteBlock":
      return {
        id: blockId,
        type: "blockquote",
        content: {
          text: node.attrs?.text || "",
          variant: node.attrs?.variant || "simple",
          attribution: node.attrs?.attribution || "",
        },
        sortOrder,
      };

    case "footnoteBlockNode":
      return {
        id: blockId,
        type: "footnote",
        content: {
          text: node.attrs?.text || "",
        },
        sortOrder,
      };

    case "embedBlock":
      return {
        id: blockId,
        type: "embed",
        content: {
          engine: node.attrs?.engine || "latex",
          code: node.attrs?.code || "",
          caption: node.attrs?.caption || "",
          label: node.attrs?.label || "",
          previewImage: node.attrs?.previewImage || "",
          packages: node.attrs?.packages || [],
        },
        sortOrder,
      };

    case "algorithmBlock":
      return {
        id: blockId,
        type: "algorithm",
        content: {
          caption: node.attrs?.caption || "",
          label: node.attrs?.label || "",
          lines: node.attrs?.lines || [{ indent: 0, keyword: "", text: "", comment: "" }],
        },
        sortOrder,
      };

    case "calloutBlock":
      return {
        id: blockId,
        type: "callout",
        content: {
          calloutType: node.attrs?.calloutType || "note",
          title: node.attrs?.title || "",
          text: node.attrs?.text || "",
          collapsible: node.attrs?.collapsible || false,
          color: node.attrs?.color || "",
        },
        sortOrder,
      };

    case "personalInfoBlock":
      return {
        id: blockId,
        type: "personalInfo",
        content: {
          name: node.attrs?.name || "",
          headline: node.attrs?.headline || "",
          email: node.attrs?.email || "",
          phones: node.attrs?.phones || [],
          homepage: node.attrs?.homepage || "",
          location: node.attrs?.location || "",
          socials: node.attrs?.socials || [],
          extra: node.attrs?.extra || "",
        },
        sortOrder,
      };

    case "photoBlock":
      return {
        id: blockId,
        type: "photo",
        content: {
          src: node.attrs?.src || "",
          alt: node.attrs?.alt || "",
          shape: node.attrs?.shape || "square",
          size: node.attrs?.size ?? 64,
          position: node.attrs?.position || "right",
          border: node.attrs?.border ?? 0,
        },
        sortOrder,
      };

    case "cvSectionBlock":
      return {
        id: blockId,
        type: "cvSection",
        content: { title: node.attrs?.title || "" },
        sortOrder,
      };

    case "cvEntryBlock":
      return {
        id: blockId,
        type: "cvEntry",
        content: {
          period: node.attrs?.period || "",
          role: node.attrs?.role || "",
          org: node.attrs?.org || "",
          location: node.attrs?.location || "",
          highlight: node.attrs?.highlight || "",
          description: node.attrs?.description || "",
          tech: node.attrs?.tech || [],
        },
        sortOrder,
      };

    default:
      return null;
  }
}
